import type {HotelDetails} from '@/api/hoteldetails';
import type {SortParams} from '@/modules/common/context/QueryParams/models';
import type {
  EventAction,
  EventActionCreator,
  EventHandler,
  ProductEcommerceItem,
} from '@/redux/middleware/googleTagManager/types';
import type {Price} from '@/redux/v1/models';
import type {BestOffer} from '@/redux/v1/offers/list/models';
import type {LooseString} from '@/types';

import {setConversionData} from '@find/sponsored-ad-tracking';

import {OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON} from '@/modules/offers/components/OfferBox/models';
import {
  OFFER_DETAILS_OVERLAY_SECTION_FLIGHT_DETAILS,
  OFFER_DETAILS_OVERLAY_SECTION_OFFER_DETAILS,
} from '@/modules/offers/components/OfferDetailsOverlay/models';
import {EVENT_ADD_OFFER_TO_CART} from '@/redux/middleware/googleTagManager/actionTypes';
import {mapBestOfferType} from '@/redux/middleware/googleTagManager/utils/mapBestOfferType';
import {mapProductEcommerceItem} from '@/redux/middleware/googleTagManager/utils/productData';
import {waitForGtmDomEvent} from '@/redux/middleware/googleTagManager/utils/waitForGtmDomEvent';
import {
  selectAvailability,
  selectMetaAutovacancy,
} from '@/redux/v1/availability/reducer';
import {selectOffer} from '@/redux/v1/offers/list/reducer';

type Payload = {
  index: number;
  hotelId: string;
  offerId: string;
  origin: string;
  bestOffer?: null | BestOffer;
  isBestOfferBox: boolean;
  upsellingOfferPrice: null | Price;
  upsellingUpgradeIds: Array<string>;
  hotelDetails: HotelDetails;
  alternativeFlightPrice: null | Price;
  travellerCount: number;
  sortParams: SortParams;
};

type Action = EventAction<Payload>;

type NullEvent = {
  ecommerce: null;
};

type Event = {
  event: string;
  event_action: string;
  is_ad: '1 - sponsored' | '0';
  ad_url: LooseString<'0'>;
  booking_session_id: string;
  non_interaction: false;
  autovacancy: string;
  ecommerce: {
    items: Array<ProductEcommerceItem>;
  };
};

export const action: EventActionCreator<Action, Payload> = (payload) => ({
  type: EVENT_ADD_OFFER_TO_CART,
  payload,
});

const mapOriginToEventAction = (origin: string, isBestOfferBox: boolean) => {
  switch (origin) {
    case OFFER_DETAILS_OVERLAY_SECTION_OFFER_DETAILS: {
      return isBestOfferBox
        ? 'click_offer_in_best_offer_box_offer_details'
        : 'click_offer_in_offer_details';
    }
    case OFFER_DETAILS_OVERLAY_SECTION_FLIGHT_DETAILS: {
      return isBestOfferBox
        ? 'click_offer_in_best_offer_box_flight_details'
        : 'click_offer_in_flight_details';
    }
    case OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON: {
      return isBestOfferBox
        ? 'click_offer_in_best_offer_box'
        : 'click_offer_in_list';
    }
    default: {
      return 'click_offer_in_unknown';
    }
  }
};

export const handler: EventHandler<
  Action,
  Promise<[NullEvent, Event]>
> = waitForGtmDomEvent<[NullEvent, Event], Payload>((eventAction, state) => {
  const {
    index,
    hotelId,
    offerId,
    origin,
    bestOffer,
    isBestOfferBox,
    upsellingOfferPrice,
    upsellingUpgradeIds,
    alternativeFlightPrice,
    hotelDetails,
    travellerCount,
    sortParams,
  } = eventAction.payload;
  const offer = selectOffer({hotelId, offerId}, state);
  const availability = selectAvailability(offerId, state);
  const autoVacancy = selectMetaAutovacancy(offerId, state);

  if (offer?.sponsored) {
    try {
      setConversionData(hotelId, {
        type: 'offer-listing',
        trackingUrl: offer.sponsored.conversionUrl,
      });
    } catch {
      // ignore
    }
  }

  return [
    {ecommerce: null},
    {
      event: 'add_to_cart',
      event_action: mapOriginToEventAction(origin, isBestOfferBox),
      booking_session_id: availability?.id || '',
      non_interaction: false,
      ad_url: offer?.sponsored?.clickUrl ?? '0',
      is_ad: offer?.sponsored?.clickUrl ? '1 - sponsored' : '0',
      autovacancy: autoVacancy ? '' : '0',
      ecommerce: {
        items: [
          mapProductEcommerceItem({
            hotelId,
            hotelData: hotelDetails,
            offerData: availability,
            travellerCount,
            index,
            supplier: offer?.supplier,
            sortParams: sortParams?.sortBy || '',
            itemBestOfferType: bestOffer?.type
              ? mapBestOfferType(bestOffer?.type)
              : 'no best offer',
            upsellingOfferPrice,
            upsellingUpgradeIds,
            alternativeFlightPrice,
          }),
        ],
      },
    },
  ];
});
